import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import { IoMdCheckmarkCircleOutline } from 'react-icons/io';

import BasicButton from '../../../../components/buttons/basicButton';
import { SearchableSelectType } from '../../../../components/inputs/searchableSelect';
import TextInput from '../../../../components/inputs/textInput';
import AampeIconBackground from '../../../../components/shared/AampeIconBackground/AampeIconBackground';
import AampeTooltip from '../../../../components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';
import { FormulaType } from '../../../../constants/formulaType';

import { EditorContext } from '../../../../context/EditorContext';

import useSingleTestMessageUpdate from '../../../../hooks/contact_notification_token/UseSingleTestMessageUpdate';
import useGetGroupTestMessageUsers from '../../../../hooks/contact_notification_token/useGetGroupTestMessageUsers';
import useSingleTestMessageCreate from '../../../../hooks/contact_notification_token/useSingleTestMessageCreate';

import { GroupTestMessageSettingModal } from '../../../../models/contact_notification_token';
import {
    FormulaTypesData,
    TestFormulaContent,
} from '../../../../models/formula.model';

import TestNotificationBox from './TestNotificationBox';

interface TestFormulaSuccessProps {
    contactId: string[];
    deeplink: string;
    testMessageContent?: TestFormulaContent | null;
    handleNotify: () => void;
    selectedToken?: SearchableSelectType | null;
    reset: () => void;
    providerId?: string;
    groupId?: string;
}

const TestFormulaSuccess: FC<React.PropsWithChildren<TestFormulaSuccessProps>> =
    ({
        contactId,
        deeplink,
        testMessageContent,
        handleNotify,
        selectedToken,
        reset,
        providerId,
        groupId,
    }) => {
        const createToken = useSingleTestMessageCreate();
        const updateToken = useSingleTestMessageUpdate(
            createToken?.data?.data?.id as string,
        );

        const { data: groupTokens } = useGetGroupTestMessageUsers({
            push_provider_id: providerId,
        });

        const {
            editorDetails: { formulaType },
        } = useContext(EditorContext);

        const [tokenName, setTokenName] = useState<string>('');
        const [edit, setEdit] = useState<boolean>(true);

        const created = useMemo<boolean>(
            () => Boolean(createToken.data?.data),
            [createToken],
        );

        const testMessageInfo =
            formulaType && FormulaTypesData[formulaType].testMessageInfo;

        const handleSave = useCallback(() => {
            if (created) {
                updateToken.mutate(
                    {
                        name: tokenName,
                        token: contactId[0],
                    },
                    { onSuccess: () => setEdit(false) },
                );
            } else {
                createToken.mutate(
                    {
                        name: tokenName,
                        token: contactId[0],
                        push_provider_id: providerId!,
                    },
                    { onSuccess: () => setEdit(false) },
                );
            }
        }, [
            created,
            updateToken,
            tokenName,
            contactId,
            createToken,
            providerId,
        ]);

        const handleReset = useCallback(() => {
            createToken.reset();
            updateToken.reset();
            setTokenName('');
            setEdit(true);
            reset();
        }, [updateToken, createToken, reset]);

        const testMessageTypeLabel = testMessageInfo
            ? testMessageInfo.label.toLowerCase()
            : 'token';

        const groupData = useMemo<
            GroupTestMessageSettingModal | undefined
        >(() => {
            if (groupId && groupTokens) {
                return groupTokens?.find(
                    (groupToken) => groupToken.id === groupId,
                );
            }
            return undefined;
        }, [groupId, groupTokens]);

        return (
            <>
                <AampeIconBackground
                    primaryColor={aampeTheme.colors.success50}
                    secondaryColor={aampeTheme.colors.success100}
                >
                    <IoMdCheckmarkCircleOutline
                        color={aampeTheme.colors.success500}
                    />
                </AampeIconBackground>
                <Heading>Success!</Heading>
                <Paragraph>
                    A test message was sent to{' '}
                    {!groupId ? (
                        <>
                            {testMessageTypeLabel}:
                            <span style={{ fontWeight: 'bold' }}>
                                {selectedToken
                                    ? `${selectedToken.label} (xxxxx)`
                                    : 'xxxxx'}
                                .
                            </span>
                        </>
                    ) : (
                        <>
                            <span style={{ fontWeight: 'bold' }}>
                                {contactId?.length === groupData?.users?.length
                                    ? groupData?.name
                                    : contactId
                                          ?.map(
                                              (user) =>
                                                  groupData?.users?.find(
                                                      (item) =>
                                                          item?.token === user,
                                                  )?.name,
                                          )
                                          .join(', ')}
                            </span>
                        </>
                    )}{' '}
                    You should expect to receive it within 1-2 minutes.
                </Paragraph>
                <Box>
                    {formulaType !== FormulaType.email && (
                        <TestNotificationBox
                            testMessageContent={testMessageContent}
                        />
                    )}
                </Box>
                {deeplink && (
                    <DeepLink>
                        <LinkTitle>Deeplink</LinkTitle>
                        <AampeTooltip arrow title={deeplink}>
                            <Link>{deeplink}</Link>
                        </AampeTooltip>
                    </DeepLink>
                )}
                {!selectedToken && !groupId && (
                    <SaveTokenContainer>
                        <TextInput
                            label={`Remember ${testMessageTypeLabel} (Optional)`}
                            onChange={(e) => setTokenName(e.target.value)}
                            value={tokenName}
                            placeholder={'Add your name'}
                            isDisabled={
                                createToken.isPending ||
                                updateToken.isPending ||
                                !edit
                            }
                        />
                        {created ? (
                            <>
                                {!edit ? (
                                    <BasicButton
                                        name={'Edit'}
                                        isPrimary={false}
                                        onClick={() => setEdit(true)}
                                    />
                                ) : (
                                    <BasicButton
                                        name={'Save'}
                                        isPrimary
                                        onClick={handleSave}
                                        isLoading={updateToken.isPending}
                                    />
                                )}
                            </>
                        ) : (
                            <BasicButton
                                name={'Save'}
                                isPrimary
                                onClick={handleSave}
                                isLoading={createToken.isPending}
                            />
                        )}
                    </SaveTokenContainer>
                )}
                <FooterParagraph>
                    Didn't receive a message?{' '}
                    <FooterLink onClick={handleNotify}>
                        Click here to notify us.
                    </FooterLink>
                </FooterParagraph>
                <FooterParagraph>
                    <FooterLink onClick={handleReset}>Test again</FooterLink>
                </FooterParagraph>
            </>
        );
    };

const Heading = styled.h1(() => ({
    color: aampeTheme.colors.success500,
    fontSize: '20px',
}));

const Paragraph = styled.p(() => ({
    paddingTop: aampeTheme.spacing(1),
    fontSize: '14px',
    textAlign: 'center',
}));

const Box = styled.div(() => ({
    margin: aampeTheme.spacing(4, 0),
}));

const DeepLink = styled.div(() => ({
    width: '400px',
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    padding: aampeTheme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
}));

const LinkTitle = styled.p(() => ({
    fontWeight: 'bold',
}));

const Link = styled.a(() => ({
    fontSize: '14px',
    cursor: 'pointer',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '240px',
}));

const FooterParagraph = styled.p(() => ({
    fontSize: '14px',
}));

const FooterLink = styled.a(() => ({
    color: aampeTheme.colors.solidA01,
    cursor: 'pointer',
    fontWeight: 600,
}));

const SaveTokenContainer = styled.div(() => ({
    display: 'flex',
    marginTop: aampeTheme.spacing(2),
    alignItems: 'end',
    gap: aampeTheme.spacing(2),
}));

export default TestFormulaSuccess;
